import React from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useTouchGestures } from '../../utils/useTouchGestures'

/**
 * ZoomableImage - A reusable component for images with pinch-zoom, double-tap zoom, pan, and swipe gestures
 * 
 * Supports two usage patterns:
 * 
 * 1. Props-based (for carousels with navigation):
 *    <ZoomableImage 
 *      src={imageUrl} 
 *      imageKey={index} 
 *      onSwipeLeft={nextPhoto} 
 *      onSwipeRight={prevPhoto} 
 *    />
 * 
 * 2. Children-based (wrapper mode for single images):
 *    <ZoomableImage>
 *      <Image src={url} fill style={{objectFit: 'contain'}} />
 *    </ZoomableImage>
 * 
 * @param {Object} props
 * @param {string} props.src - Image source URL (props-based mode)
 * @param {string} props.alt - Image alt text
 * @param {number} props.imageKey - Unique key for AnimatePresence transitions (e.g., current index)
 * @param {Function} props.onSwipeLeft - Callback for swipe left (next)
 * @param {Function} props.onSwipeRight - Callback for swipe right (prev)
 * @param {number} props.maxScale - Maximum zoom scale (default: 5)
 * @param {number} props.doubleTapScale - Scale on double tap (default: 2.5)
 * @param {boolean} props.showZoomIndicator - Show zoom level indicator (default: true)
 * @param {boolean} props.showHint - Show gesture hint text (default: true)
 * @param {string} props.hintText - Custom hint text
 * @param {Function} props.onZoomChange - Callback when zoom changes (receives { scale, isZoomed })
 * @param {React.ReactNode} props.children - Children to wrap with zoom capability (wrapper mode)
 * @param {string} props.className - Additional className for outer container
 */
export const ZoomableImage = ({
    src,
    alt = 'Image',
    imageKey,
    onSwipeLeft,
    onSwipeRight,
    maxScale = 5,
    doubleTapScale = 2.5,
    showZoomIndicator = true,
    showHint = true,
    hintText = 'Pinch to zoom · Swipe to navigate',
    onZoomChange,
    children,
    className = '',
}) => {
    const {
        scale,
        isZoomed,
        resetZoom,
        containerRef,
        handlers,
        transformStyle,
    } = useTouchGestures({
        maxScale,
        doubleTapScale,
        onSwipeLeft: onSwipeLeft ? () => {
            resetZoom()
            onSwipeLeft?.()
        } : undefined,
        onSwipeRight: onSwipeRight ? () => {
            resetZoom()
            onSwipeRight?.()
        } : undefined,
        enabled: true,
    })

    // Call onZoomChange callback when scale changes
    React.useEffect(() => {
        onZoomChange?.({ scale, isZoomed })
    }, [scale, isZoomed, onZoomChange])

    // Determine if we're in wrapper mode (children provided, no src)
    const isWrapperMode = !src && children

    return (
        <div className={`relative overflow-hidden select-none ${isWrapperMode ? 'h-full w-full' : ''} ${className}`}>
            {/* Touch capture layer */}
            <div
                ref={containerRef}
                className={`${isWrapperMode ? 'h-full w-full' : 'absolute inset-0'} flex items-center justify-center`}
                style={{ touchAction: 'none' }}
                {...handlers}
            >
                {/* Zoom / pan transform wrapper */}
                <div
                    style={{
                        ...transformStyle,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: isWrapperMode ? 'relative' : undefined,
                    }}
                >
                    {isWrapperMode ? (
                        // Wrapper mode: render children directly
                        children
                    ) : src ? (
                        // Props-based mode: render image with AnimatePresence
                        <AnimatePresence mode="wait">
                            <motion.img
                                key={imageKey}
                                src={src}
                                alt={alt}
                                className="max-w-full max-h-full object-contain"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.15 }}
                                draggable={false}
                                style={{ pointerEvents: 'none', userSelect: 'none' }}
                            />
                        </AnimatePresence>
                    ) : null}
                </div>
            </div>

            {/* Zoom hint badge — shown only at scale 1 (only in props mode or if explicitly enabled) */}
            {showHint && !isZoomed && !isWrapperMode && (
                <div className="absolute bottom-2 right-3 z-10 text-white/40 text-[10px] pointer-events-none">
                    {hintText}
                </div>
            )}

            {/* Zoom indicator */}
            {showZoomIndicator && isZoomed && (
                <div className="absolute top-3 right-3 z-10 bg-black/50 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-full pointer-events-none">
                    {scale.toFixed(1)}×
                </div>
            )}
        </div>
    )
}

/**
 * Hook wrapper for components that need more control
 * Re-exports useTouchGestures for direct usage
 */
export { useTouchGestures } from '../../utils/useTouchGestures'

export default ZoomableImage
